import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Play, 
  Pause, 
  Settings, 
  RefreshCw, 
  FileText, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  ArrowRight,
  Layers,
  Target,
  Route,
  Inbox
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Pipeline Stage Component
function PipelineStage({ icon: Icon, title, count, total, color, isActive }) {
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
  
  return (
    <div className={`relative flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
      isActive ? 'border-primary bg-primary/5' : 'border-border bg-white'
    }`}>
      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
      <span className="text-sm font-medium text-foreground">{title}</span>
      <span className="text-2xl font-bold text-foreground mt-1">{count}</span>
      <span className="text-xs text-muted-foreground">of {total}</span>
      <Progress value={percentage} className="w-full mt-2 h-1.5" />
    </div>
  );
}

// Queue Card Component
function QueueCard({ name, count, amount, priority, overdue, atRisk }) {
  const getQueueColor = (queueName) => {
    const colors = {
      'AUTO_POST': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'REVIEW_HIGH': 'bg-amber-50 text-amber-700 border-amber-200',
      'REVIEW': 'bg-blue-50 text-blue-700 border-blue-200',
      'APPEALS': 'bg-purple-50 text-purple-700 border-purple-200',
      'MANUAL': 'bg-slate-50 text-slate-700 border-slate-200',
      'EXCEPTIONS': 'bg-red-50 text-red-700 border-red-200',
      'PATIENT_BILLING': 'bg-teal-50 text-teal-700 border-teal-200'
    };
    return colors[queueName] || 'bg-muted text-foreground border-border';
  };

  const formatQueueName = (name) => {
    return name.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  return (
    <div className={`p-4 rounded-lg border ${getQueueColor(name)}`} data-testid={`queue-card-${name.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium">{formatQueueName(name)}</span>
        <Badge variant="secondary" className="text-xs">{count}</Badge>
      </div>
      {amount > 0 && (
        <div className="text-sm opacity-80">${amount.toLocaleString()}</div>
      )}
      <div className="flex gap-2 mt-2">
        {overdue > 0 && (
          <Badge variant="destructive" className="text-xs">
            {overdue} overdue
          </Badge>
        )}
        {atRisk > 0 && (
          <Badge variant="outline" className="text-xs border-amber-400 text-amber-600">
            {atRisk} at risk
          </Badge>
        )}
      </div>
    </div>
  );
}

export default function WorkflowPage() {
  const queryClient = useQueryClient();
  const [selectedAutomation, setSelectedAutomation] = useState('semi_auto');

  // Fetch workflow status
  const { data: workflowStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['workflowStatus'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/workflow/status`);
      if (!res.ok) throw new Error('Failed to fetch workflow status');
      return res.json();
    },
    refetchInterval: 5000
  });

  // Fetch workflow config
  const { data: workflowConfig, isLoading: configLoading } = useQuery({
    queryKey: ['workflowConfig'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/workflow/config`);
      if (!res.ok) throw new Error('Failed to fetch workflow config');
      return res.json();
    }
  });

  // Fetch routing stats
  const { data: routingStats } = useQuery({
    queryKey: ['routingStats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/route/stats`);
      if (!res.ok) throw new Error('Failed to fetch routing stats');
      return res.json();
    },
    refetchInterval: 5000
  });

  // Fetch pending documents
  const { data: pendingDocs } = useQuery({
    queryKey: ['pendingDocs'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/documents?status=complete&limit=10`);
      if (!res.ok) throw new Error('Failed to fetch documents');
      return res.json();
    }
  });

  // Run batch pipeline mutation
  const runPipelineMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/workflow/pipeline/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pending_only: true })
      });
      if (!res.ok) throw new Error('Failed to run pipeline');
      return res.json();
    },
    onSuccess: (data) => {
      toast.success(`Pipeline completed: ${data.successful} successful, ${data.partial} partial, ${data.failed} failed`);
      queryClient.invalidateQueries(['workflowStatus']);
      queryClient.invalidateQueries(['routingStats']);
    },
    onError: (error) => {
      toast.error(`Pipeline failed: ${error.message}`);
    }
  });

  // Update config mutation
  const updateConfigMutation = useMutation({
    mutationFn: async (updates) => {
      const res = await fetch(`${API_URL}/api/workflow/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (!res.ok) throw new Error('Failed to update config');
      return res.json();
    },
    onSuccess: () => {
      toast.success('Configuration updated');
      queryClient.invalidateQueries(['workflowConfig']);
    }
  });

  const handleAutomationChange = (value) => {
    setSelectedAutomation(value);
    updateConfigMutation.mutate({
      automation_level: value,
      auto_classify: value !== 'manual',
      auto_detect: value !== 'manual',
      auto_route: value === 'full_auto'
    });
  };

  if (statusLoading || configLoading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          <Skeleton className="h-10 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  const docs = workflowStatus?.documents || {};
  const workItems = workflowStatus?.work_items || {};
  const queueStats = routingStats?.by_queue || {};

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="workflow-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground font-heading">Document Workflow</h1>
          <p className="text-muted-foreground mt-1">Configure and monitor the document processing pipeline</p>
        </div>
        <div className="flex items-center gap-3 mt-4 sm:mt-0">
          <Button
            onClick={() => runPipelineMutation.mutate()}
            disabled={runPipelineMutation.isPending || docs.pending_pipeline === 0}
            data-testid="run-pipeline-btn"
          >
            {runPipelineMutation.isPending ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Run Pipeline
          </Button>
        </div>
      </div>

      {/* Pipeline Status */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Layers className="w-5 h-5 text-primary" />
            Pipeline Status
          </CardTitle>
          <CardDescription>
            {docs.pending_pipeline > 0 
              ? `${docs.pending_pipeline} document(s) pending processing`
              : 'All documents have been processed'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <PipelineStage 
              icon={Inbox} 
              title="Extracted" 
              count={docs.total_extracted || 0}
              total={docs.total_extracted || 0}
              color="bg-blue-100 text-blue-600"
              isActive={true}
            />
            <div className="hidden md:flex items-center justify-center">
              <ArrowRight className="w-6 h-6 text-muted-foreground" />
            </div>
            <PipelineStage 
              icon={Layers} 
              title="Classified" 
              count={docs.classified || 0}
              total={docs.total_extracted || 0}
              color="bg-purple-100 text-purple-600"
              isActive={docs.classified < docs.total_extracted}
            />
            <div className="hidden md:flex items-center justify-center">
              <ArrowRight className="w-6 h-6 text-muted-foreground" />
            </div>
            <PipelineStage 
              icon={Target} 
              title="Detected" 
              count={docs.detected || 0}
              total={docs.total_extracted || 0}
              color="bg-amber-100 text-amber-600"
              isActive={docs.detected < docs.total_extracted}
            />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
            <div className="col-span-2 md:col-start-2"></div>
            <div className="hidden md:flex items-center justify-center">
              <ArrowRight className="w-6 h-6 text-muted-foreground" />
            </div>
            <PipelineStage 
              icon={Route} 
              title="Routed" 
              count={docs.routed || 0}
              total={docs.total_extracted || 0}
              color="bg-teal-100 text-teal-600"
              isActive={docs.routed < docs.total_extracted}
            />
            <div className="hidden md:flex items-center justify-center">
              <ArrowRight className="w-6 h-6 text-muted-foreground" />
            </div>
            <PipelineStage 
              icon={CheckCircle2} 
              title="Complete" 
              count={docs.pipeline_complete || 0}
              total={docs.total_extracted || 0}
              color="bg-emerald-100 text-emerald-600"
              isActive={false}
            />
          </div>

          {/* Completion Rate */}
          <div className="mt-6 p-4 rounded-lg bg-muted/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Pipeline Completion Rate</span>
              <span className="text-lg font-bold text-primary">{workflowStatus?.pipeline_completion_rate || 0}%</span>
            </div>
            <Progress value={workflowStatus?.pipeline_completion_rate || 0} className="h-2" />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration Panel */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Pipeline Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Automation Level */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Automation Level
              </label>
              <Select 
                value={workflowConfig?.automation_level || 'semi_auto'} 
                onValueChange={handleAutomationChange}
                data-testid="automation-level-select"
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual - Trigger each stage</SelectItem>
                  <SelectItem value="semi_auto">Semi-Auto - Auto classify & detect</SelectItem>
                  <SelectItem value="full_auto">Full-Auto - Complete pipeline</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Toggle Switches */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Auto-Classify</div>
                  <div className="text-xs text-muted-foreground">Automatically classify documents</div>
                </div>
                <Switch 
                  checked={workflowConfig?.auto_classify || false}
                  onCheckedChange={(checked) => updateConfigMutation.mutate({ auto_classify: checked })}
                  data-testid="auto-classify-switch"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Auto-Detect</div>
                  <div className="text-xs text-muted-foreground">Automatically detect denials</div>
                </div>
                <Switch 
                  checked={workflowConfig?.auto_detect || false}
                  onCheckedChange={(checked) => updateConfigMutation.mutate({ auto_detect: checked })}
                  data-testid="auto-detect-switch"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">Auto-Route</div>
                  <div className="text-xs text-muted-foreground">Automatically route to work queues</div>
                </div>
                <Switch 
                  checked={workflowConfig?.auto_route || false}
                  onCheckedChange={(checked) => updateConfigMutation.mutate({ auto_route: checked })}
                  data-testid="auto-route-switch"
                />
              </div>
            </div>

            {/* SLA Info */}
            <div className="pt-4 border-t">
              <div className="text-sm font-medium mb-3">SLA Settings (hours)</div>
              <div className="space-y-2 text-sm">
                {Object.entries(workflowConfig?.sla_settings || {}).map(([queue, hours]) => (
                  <div key={queue} className="flex justify-between">
                    <span className="text-muted-foreground">
                      {queue.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')}
                    </span>
                    <span className="font-medium">{hours}h</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Work Queues */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Route className="w-5 h-5 text-primary" />
              Work Queues
            </CardTitle>
            <CardDescription>
              {workItems.pending || 0} pending • {workItems.in_progress || 0} in progress
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="overview">
              <TabsList className="mb-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="sla">SLA Status</TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(workItems.by_queue || {}).map(([queue, count]) => (
                    <QueueCard 
                      key={queue}
                      name={queue}
                      count={count}
                      amount={queueStats[queue]?.total_amount || 0}
                      priority={queueStats[queue]?.avg_priority || 0}
                      overdue={0}
                      atRisk={0}
                    />
                  ))}
                  {Object.keys(workItems.by_queue || {}).length === 0 && (
                    <div className="col-span-full text-center py-8 text-muted-foreground">
                      <Route className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No documents in work queues</p>
                      <p className="text-sm mt-1">Run the pipeline to route documents</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="sla">
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="p-4 rounded-lg bg-emerald-50 text-center">
                    <CheckCircle2 className="w-6 h-6 mx-auto text-emerald-600 mb-1" />
                    <div className="text-2xl font-bold text-emerald-700">
                      {routingStats?.sla?.on_time || 0}
                    </div>
                    <div className="text-xs text-emerald-600">On Time</div>
                  </div>
                  <div className="p-4 rounded-lg bg-amber-50 text-center">
                    <Clock className="w-6 h-6 mx-auto text-amber-600 mb-1" />
                    <div className="text-2xl font-bold text-amber-700">
                      {routingStats?.sla?.at_risk || 0}
                    </div>
                    <div className="text-xs text-amber-600">At Risk</div>
                  </div>
                  <div className="p-4 rounded-lg bg-red-50 text-center">
                    <AlertTriangle className="w-6 h-6 mx-auto text-red-600 mb-1" />
                    <div className="text-2xl font-bold text-red-700">
                      {routingStats?.sla?.overdue || 0}
                    </div>
                    <div className="text-xs text-red-600">Overdue</div>
                  </div>
                </div>

                <div className="text-sm text-muted-foreground">
                  SLA compliance helps ensure timely processing of all documents.
                  Items marked &quot;At Risk&quot; have less than 25% of their SLA time remaining.
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Pending Documents Table */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Documents Pending Pipeline
          </CardTitle>
          <CardDescription>
            Documents that have been extracted but not yet processed through the full pipeline
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pendingDocs?.documents?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead>Payer</TableHead>
                  <TableHead>Classification</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uploaded</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingDocs.documents
                  .filter(doc => !doc.pipeline_completed_at)
                  .slice(0, 10)
                  .map((doc) => (
                    <TableRow key={doc.id} data-testid="pending-doc-row">
                      <TableCell className="font-medium">{doc.filename}</TableCell>
                      <TableCell>{doc.payer_name || 'Unknown'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{doc.classification_type || 'Pending'}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          className={
                            doc.classified_at ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                          }
                        >
                          {doc.classified_at ? 'Classified' : 'Needs Processing'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(doc.upload_date).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>All documents have been processed</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
